import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { Card, CardContent } from './ui/card';
import { Loader2 } from 'lucide-react';

/**
 * RawMaterialStockChart
 *
 * Displays a bar chart of raw material stock quantities.
 * Supports zoom (mouse wheel) and pan (drag) via ECharts dataZoom.
 *
 * Props:
 *   - data: Array of inventory items with fields:
 *       { product_id, name, specification, category, unit, product_code,
 *         location, remaining_quantity, reserved_quantity, available_quantity }
 *   - loading: Boolean
 *   - excludeZeroStock: Boolean (affects empty state messaging)
 */
const RawMaterialStockChart = ({ data, loading, excludeZeroStock = false }) => {
  const chartOption = useMemo(() => {
    if (!data || data.length === 0) {
      return {
        title: {
          text: '暂无数据',
          left: 'center',
          top: 'middle',
          textStyle: { color: '#888', fontSize: 16 }
        }
      };
    }

    // Group by product_code (货号), summing quantities across all locations/clients
    const groupedMap = new Map();
    data.forEach(item => {
      const key = item.product_code || item.product_id;
      if (!groupedMap.has(key)) {
        groupedMap.set(key, {
          product_id: item.product_id,
          product_code: item.product_code,
          name: item.name,
          specification: item.specification,
          category: item.category,
          unit: item.unit,
          tip_diameter: item.tip_diameter,
          remaining_quantity: 0
        });
      }
      groupedMap.get(key).remaining_quantity += item.remaining_quantity || 0;
    });

    // Sort by tip_diameter ascending (left to right)
    const sortedData = Array.from(groupedMap.values()).sort((a, b) => {
      const da = a.tip_diameter != null ? parseFloat(a.tip_diameter) : Infinity;
      const db = b.tip_diameter != null ? parseFloat(b.tip_diameter) : Infinity;
      return da - db;
    });

    const categories = sortedData.map(item => item.specification || item.name || item.product_code || item.product_id);
    const remainingValues = sortedData.map(item => item.remaining_quantity);

    // Determine bar color based on stock level
    const getBarColor = (value) => {
      if (value <= 0) return '#ef4444'; // red - out of stock
      if (value < 10) return '#f59e0b'; // amber - low stock
      return '#3b82f6'; // blue - healthy
    };

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (params) => {
          const idx = params[0]?.dataIndex ?? 0;
          const item = sortedData[idx];
          if (!item) return '';
          return `
            <div style="font-weight:600;margin-bottom:4px">${item.specification || item.name}</div>
            <div>产品编号: ${item.product_id}</div>
            <div>货号: ${item.product_code || '-'}</div>
            <div>分类: ${item.category || '-'}</div>
            ${item.tip_diameter != null ? `<div>刃径: ${item.tip_diameter} mm</div>` : ''}
            <div style="margin-top:4px">
              <span style="display:inline-block;width:10px;height:10px;background:#3b82f6;border-radius:50%;margin-right:4px"></span>
              库存数量: <strong>${item.remaining_quantity} ${item.unit}</strong>
            </div>
          `;
        }
      },
      legend: {
        show: false
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '15%',
        top: '10%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: categories,
        axisLabel: {
          interval: 0,
          rotate: 45,
          fontSize: 11,
          color: '#666'
        },
        axisTick: { alignWithLabel: true }
      },
      yAxis: {
        type: 'value',
        name: '数量',
        nameTextStyle: { padding: [0, 0, 0, 20] }
      },
      dataZoom: [
        {
          type: 'inside',
          xAxisIndex: 0,
          zoomOnMouseWheel: true,
          moveOnMouseMove: true,
          moveOnMouseWheel: true,
          preventDefaultMouseMove: false
        },
        {
          type: 'slider',
          xAxisIndex: 0,
          bottom: 10,
          height: 20,
          handleIcon: 'path://M10.7,11.9v-1.3H9.3v1.3c-4.9,0.3-8.8,4.4-8.8,9.4c0,5,3.9,9.1,8.8,9.4v1.3h1.3v-1.3c4.9-0.3,8.8-4.4,8.8-9.4C19.5,16.3,15.6,12.2,10.7,11.9z M13.3,24.4H6.7V23h6.6V24.4z M13.3,19.6H6.7v-1.4h6.6V19.6z',
          handleSize: '80%',
          handleStyle: {
            color: '#fff',
            shadowBlur: 3,
            shadowColor: 'rgba(0, 0, 0, 0.6)',
            shadowOffsetX: 2,
            shadowOffsetY: 2
          },
          textStyle: { color: '#666' },
          borderColor: '#ddd',
          fillerColor: 'rgba(59, 130, 246, 0.1)'
        }
      ],
      series: [
        {
          name: '库存数量',
          type: 'bar',
          data: remainingValues,
          itemStyle: {
            color: (params) => getBarColor(params.value),
            borderRadius: [4, 4, 0, 0]
          },
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowColor: 'rgba(0,0,0,0.3)'
            }
          },
          animationDelay: (idx) => idx * 10
        }
      ],
      animationEasing: 'elasticOut',
      animationDelayUpdate: (idx) => idx * 5
    };
  }, [data]);

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8">
          <div className="flex justify-center items-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary mr-3" />
            <span className="text-muted-foreground">加载图表数据...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card>
        <CardContent className="p-8">
          <div className="text-center py-12">
            <p className="text-muted-foreground text-lg">
              {excludeZeroStock ? '没有符合条件的原材料库存数据' : '暂无原材料库存数据'}
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              {excludeZeroStock ? '请尝试取消"隐藏零库存"选项' : '请先入库一些原材料'}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="p-4">
        <div className="mb-2 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold">原材料库存图表</h3>
            <p className="text-sm text-muted-foreground">
              按货号聚合 · 共 {chartOption.series?.[0]?.data?.length ?? 0} 个规格 · 鼠标滚轮缩放 · 拖拽平移 · 底部滑块缩放
            </p>
          </div>
        </div>
        <ReactECharts
          option={chartOption}
          style={{ height: 500, width: '100%' }}
          opts={{ renderer: 'canvas' }}
          notMerge={true}
          lazyUpdate={true}
        />
      </CardContent>
    </Card>
  );
};

export default RawMaterialStockChart;
